import { withDependencies, named } from '@wix/thunderbolt-ioc'
import {
	PageFeatureConfigSymbol,
	IPageWillMountHandler,
	BrowserWindowSymbol,
	BrowserWindow,
	IPageWillUnmountHandler,
	Structure,
	IStructureStore,
	ICompsLifeCycle,
	CompsLifeCycleSym,
} from '@wix/thunderbolt-symbols'
import { childQAComps, mutationQAComps } from './specialQACompTypes'
import type { ComponentsQaApiPageConfig, ComponentsTypes, ComponentTypeData } from './types'
import { name } from './symbols'

const compIdToObserverMap: Record<string, MutationObserver> = {}
const idToNicknameMap: Record<string, string> = {}

function setCompQaAttributes(element: Element, compType: string) {
	element.setAttribute('data-comp', compType)
	element.setAttribute('data-aid', compType)
	if (idToNicknameMap[element.id]) {
		element.setAttribute('data-nickname', idToNicknameMap[element.id])
	}
}

function setChildDataCompAttr(element: Element, selector: string, compType: string) {
	element.querySelectorAll(selector).forEach((childComp) => {
		setCompQaAttributes(childComp, compType)
	})
}

function isRepeaterComp(compType: string = '') {
	return compType.indexOf('Repeater') > -1
}

function setRepeaterChildrenAttrs(element: Element, children: Array<string>, componentsTypes: ComponentsTypes) {
	let columnIndex = 0

	element.querySelectorAll(`[role='row']`).forEach((row) => {
		// eslint-disable-next-line @typescript-eslint/prefer-for-of
		for (let idx = 0; idx < row.children.length; idx++) {
			const column = row.children[idx]
			column.setAttribute('data-grid-row', idx.toString())
			column.setAttribute('data-grid-col', columnIndex.toString())
		}

		columnIndex++
	})

	children.forEach((childId) => {
		const childData = componentsTypes[childId]

		if (childData) {
			const { compType } = childData
			element.querySelectorAll(`[id^=${childId}]`).forEach((childElement) => {
				const realId = childElement.id
				addAllCompTypesToComponent(childElement, realId, compType)
			})
		}
	})
}

function getRepeaterDescendants(children: Array<string> = [], componentsTypes: ComponentsTypes): Array<string> {
	return children.reduce<Array<string>>((acc, childId) => {
		const childData = componentsTypes[childId]

		if (childData) {
			const { children: grandChildren } = childData

			if (grandChildren) {
				acc = [...acc, ...getRepeaterDescendants(grandChildren, componentsTypes)]
			}
		}

		return acc
	}, children)
}

function setRepeatersDataComps(
	element: Element,
	children: Array<string> = [],
	componentsTypes: ComponentsTypes,
	compId: string
) {
	const allRepeaterDescendants = getRepeaterDescendants(children, componentsTypes)

	const onMutation = (mutationsList: Array<MutationRecord>) => {
		mutationsList.forEach(({ type }) => {
			if (type === 'childList') {
				setRepeaterChildrenAttrs(element, allRepeaterDescendants, componentsTypes)
			}
		})
	}

	const observer = new MutationObserver(onMutation)
	observer.observe(element, { childList: true, subtree: true })
	compIdToObserverMap[compId] = observer

	setRepeaterChildrenAttrs(element, allRepeaterDescendants, componentsTypes)
}

function addChildCompTypesIfNeeded(element: Element, compId: string, compType: string) {
	const childComps = childQAComps[compType]

	if (childComps) {
		Object.keys(childComps).forEach((nestedCompType) => {
			const selector = childComps[nestedCompType]
			setChildDataCompAttr(element, selector(compId), nestedCompType)
		})
	}
}

function addMutationObserverIfNeeded(element: Element, compId: string, compType: string) {
	const mutationComps = mutationQAComps[compType]
	const onMutation = (mutationsList: Array<MutationRecord>) => {
		mutationsList.forEach(({ type, target }) => {
			if (type === 'childList') {
				Object.keys(mutationComps).forEach((mutateCompType) => {
					const selector = mutationComps[mutateCompType]
					setChildDataCompAttr(target as Element, selector(compId), mutateCompType)
				})
			}
		})
	}
	const observer = mutationComps ? new MutationObserver(onMutation) : null

	if (observer) {
		observer.observe(element, { childList: true, subtree: true })
		compIdToObserverMap[compId] = observer
		return true
	}
	return false
}

function addAllCompTypesToComponent(element: Element, compId: string, compType: string) {
	setCompQaAttributes(element, compType)
	addChildCompTypesIfNeeded(element, compId, compType)
	return addMutationObserverIfNeeded(element, compId, compType)
}

export async function setDataCompOnElement(
	compId: string,
	compTypeData: ComponentTypeData,
	componentsTypes: ComponentsTypes,
	element: Element
) {
	const { compType, children } = compTypeData

	if (element) {
		addAllCompTypesToComponent(element, compId, compType)

		if (isRepeaterComp(compType)) {
			setRepeatersDataComps(element, children, componentsTypes, compId)
			return true
		}
	}
	return false
}

export async function addDynamicComponentListener(
	componentsTypes: ComponentsTypes,
	window: NonNullable<BrowserWindow>,
	structureStore: IStructureStore
) {
	structureStore.subscribeToChanges((store) => {
		const dynamicStructureContainer = store.DYNAMIC_STRUCTURE_CONTAINER
		if (dynamicStructureContainer) {
			const { components = [] } = dynamicStructureContainer
			components.forEach((compId) => {
				const compData = structureStore.get(compId)
				const element = window.document.getElementById(compId)

				if (element) {
					setCompQaAttributes(element, compData.componentType)
					const parent = element.parentElement

					if (parent) {
						parent.setAttribute('data-aid', 'aspectCompsContainer')
					}
				}
			})
		}
	})
}

const DATA_COMP_CALLBACK = 'dataCompCallback'

export async function setDataCompOnElements(
	componentsTypes: ComponentsTypes,
	componentNicknames: Record<string, string>,
	window: NonNullable<BrowserWindow>,
	structureStore: IStructureStore,
	compsLifeCycle: ICompsLifeCycle
): Promise<() => void> {
	if (!Object.keys(componentsTypes).length) {
		return () => {}
	}
	Object.keys(componentNicknames).forEach((key) => {
		idToNicknameMap[key] = componentNicknames[key]
	})

	const observedCompIds: Array<string> = Object.keys(componentsTypes)
	await addDynamicComponentListener(componentsTypes, window, structureStore)
	return compsLifeCycle.registerToCompLifeCycle(
		observedCompIds,
		DATA_COMP_CALLBACK,
		(compId, _displayedId, element) => {
			if (element) {
				setDataCompOnElement(compId, componentsTypes[compId], componentsTypes, element)
			}
		}
	)
}

export const ComponentsQaApiPage = withDependencies(
	[named(PageFeatureConfigSymbol, name), BrowserWindowSymbol, Structure, CompsLifeCycleSym],
	(
		{ componentsTypes, componentNicknames }: ComponentsQaApiPageConfig,
		window: NonNullable<BrowserWindow>,
		structureStore: IStructureStore,
		compsLifeCycle: ICompsLifeCycle
	): IPageWillMountHandler | IPageWillUnmountHandler => {
		let unregisterFromCompLifeCycle = () => {}

		return {
			name: 'componentsQaApi',
			async pageWillMount() {
				unregisterFromCompLifeCycle = await setDataCompOnElements(
					componentsTypes,
					componentNicknames,
					window,
					structureStore,
					compsLifeCycle
				)
			},
			pageWillUnmount() {
				unregisterFromCompLifeCycle()
				Object.entries(compIdToObserverMap).forEach(([compId, observer]) => {
					observer.disconnect()
					delete compIdToObserverMap[compId]
				})
			},
		}
	}
)
